/**
 * The first-sign-in disclaimer, over the app while it loads behind.
 *
 * Wording and the accept rules are `lib/disclaimer.ts`. The app is not held
 * back: the workspace loads underneath, dimmed, and the footer says when it is
 * ready — reading costs no waiting. Nothing but an acceptance closes the card:
 * no ×, no Esc, no click outside.
 *
 * The accept zone fills as it is held, so the gesture is visibly deliberate:
 * hovered (a mouse) or pressed (touch) for its full time, or it resets. A quick
 * tap on a phone does not accept; it says to hold.
 */

import { useEffect, useRef, useState } from "react";

import { HOLD_MS, HOVER_MS, canHover, type AcceptMethod, type Disclaimer } from "../lib/disclaimer";

const BACKDROP: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  background: "rgba(0, 0, 0, 0.6)",
  backdropFilter: "blur(3px)",
  WebkitBackdropFilter: "blur(3px)",
};

const CARD: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: 760,
  maxHeight: "calc(100dvh - 32px)",
  background: "var(--qf-surface, #1f1f1f)",
  color: "var(--qf-text, #eee)",
  border: "1px solid var(--qf-border, #333)",
  borderRadius: 10,
  boxShadow: "0 18px 60px rgba(0,0,0,0.5)",
  outline: "none",
};

const BODY: React.CSSProperties = {
  overflowY: "auto",
  padding: "22px 24px 8px",
  fontSize: 15,
  lineHeight: 1.6,
};

const RED = "var(--qf-negative, #f25c5c)";

const FOOT: React.CSSProperties = {
  padding: "12px 24px 18px",
  borderTop: "1px solid var(--qf-border, #333)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const MUTED: React.CSSProperties = { color: "var(--qf-text-muted, #999)", fontSize: 13 };

export function DisclaimerGate({
  disclaimer,
  loading,
  onAccept,
}: {
  disclaimer: Disclaimer;
  /** The workspace is still loading behind the card. */
  loading: boolean;
  onAccept: (method: AcceptMethod) => void;
}) {
  const hover = canHover();
  const [filling, setFilling] = useState(false);
  const [ticked, setTicked] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const lastPointer = useRef<string>("mouse");
  const done = useRef(false);
  const card = useRef<HTMLDivElement>(null);

  useEffect(() => {
    card.current?.focus();
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const accept = (method: AcceptMethod) => {
    if (done.current) return;
    done.current = true;
    onAccept(method);
  };

  const start = (method: "hover" | "hold", ms: number) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setHint(null);
    setFilling(true);
    timer.current = window.setTimeout(() => accept(method), ms);
  };

  const stop = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    setFilling(false);
  };

  const dwell = lastPointer.current === "mouse" ? HOVER_MS : HOLD_MS;
  const idleLabel = hover ? "Hover here to accept & continue" : "Press and hold to accept & continue";

  return (
    <div style={BACKDROP} role="presentation">
      <div
        ref={card}
        style={CARD}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qf-disclaimer-title"
        tabIndex={-1}
      >
        <div style={BODY}>
          <h2 id="qf-disclaimer-title" style={{ margin: "0 0 12px", color: RED, fontSize: 24, textAlign: "center" }}>
            {disclaimer.title}
          </h2>
          <div style={{ color: RED, opacity: 0.85, fontWeight: 600, marginBottom: 14 }}>{disclaimer.heading}</div>
          <p style={{ margin: "0 0 10px" }}>{disclaimer.lead}</p>
          <ul style={{ margin: "0 0 12px", paddingLeft: 20 }}>
            {disclaimer.bullets.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {disclaimer.short.map((line) => (
            <p key={line} style={{ margin: "0 0 4px" }}>
              {line}
            </p>
          ))}
          <div style={{ fontWeight: 700, margin: "16px 0 6px" }}>Full Disclaimer:</div>
          {disclaimer.full.map((line) => (
            <p key={line} style={{ margin: "0 0 10px" }}>
              {line}
            </p>
          ))}
          <p style={{ margin: "0 0 10px" }}>
            <b>{disclaimer.use.heading}</b> {disclaimer.use.body}
          </p>
        </div>

        <div style={FOOT}>
          <button
            type="button"
            aria-label={idleLabel}
            style={{
              position: "relative",
              overflow: "hidden",
              height: 52,
              borderRadius: 8,
              border: `1px solid ${filling ? "var(--qf-positive, #3fb950)" : "var(--qf-border, #444)"}`,
              background: "var(--qf-surface-alt, #2a2a2a)",
              color: "var(--qf-text, #eee)",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
              touchAction: "manipulation",
            }}
            onContextMenu={(e) => e.preventDefault()}
            onPointerEnter={(e) => {
              lastPointer.current = e.pointerType;
              if (e.pointerType === "mouse") start("hover", HOVER_MS);
            }}
            onPointerLeave={stop}
            onPointerDown={(e) => {
              lastPointer.current = e.pointerType;
              if (e.pointerType !== "mouse") start("hold", HOLD_MS);
            }}
            onPointerUp={(e) => {
              if (e.pointerType !== "mouse" && !done.current) {
                stop();
                setHint("Keep holding until the bar fills.");
              }
            }}
            onPointerCancel={stop}
            onClick={(e) => {
              // A mouse click or Enter/Space (detail 0) is a plain yes; a tap
              // on a touch screen is not — that is what the hold is for.
              if (e.detail === 0 || lastPointer.current === "mouse") accept("click");
            }}
          >
            <span
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                width: filling ? "100%" : "0%",
                background: "var(--qf-positive, #3fb950)",
                opacity: 0.35,
                transition: filling ? `width ${dwell}ms linear` : "width 150ms ease-out",
              }}
            />
            <span style={{ position: "relative" }}>{filling ? "✓ Accepting…" : idleLabel}</span>
          </button>
          {hint && <div style={{ ...MUTED, textAlign: "center" }}>{hint}</div>}

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            <span style={MUTED}>or</span>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600 }}>
              <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
              {disclaimer.checkbox}
            </label>
            <button
              type="button"
              disabled={!ticked}
              onClick={() => accept("checkbox")}
              style={{
                padding: "6px 18px",
                borderRadius: 6,
                border: "1px solid var(--qf-border, #444)",
                background: ticked ? "var(--qf-accent, #2b6fd6)" : "transparent",
                color: ticked ? "#fff" : "var(--qf-text-muted, #999)",
                cursor: ticked ? "pointer" : "default",
                fontWeight: 600,
              }}
            >
              Continue
            </button>
          </div>

          <div style={{ ...MUTED, textAlign: "center" }} aria-live="polite">
            {loading ? "● Loading your workspace…" : "✓ Your workspace is ready"}
          </div>
        </div>
      </div>
    </div>
  );
}
